import { useState, useEffect, useMemo } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { bookingAPI } from '../services/api';
import { Clock, MapPin, ChevronLeft, ChevronRight, CheckCircle, User, Phone, Mail, AlertCircle, X, Search, XCircle, CalendarDays } from 'lucide-react';
import toast from 'react-hot-toast';

const DAYS_SHORT = ['S', 'M', 'T', 'W', 'T', 'F', 'S'];
const DAYS_FULL = ['SUN', 'MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT'];
const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
const MONTHS_SHORT = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

export default function BookingPage() {
  const { merchantId } = useParams();
  const [searchParams] = useSearchParams();
  const sourceRef = searchParams.get('ref') || '';
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [step, setStep] = useState(1); // 1=service, 2=provider, 3=calendar, 4=details
  const [selectedService, setSelectedService] = useState(null);
  const [selectedProvider, setSelectedProvider] = useState('');
  const [selectedDate, setSelectedDate] = useState('');
  const [selectedTime, setSelectedTime] = useState('');
  const [slots, setSlots] = useState({});
  const [loadingSlots, setLoadingSlots] = useState(false);
  const [form, setForm] = useState({ name: '', phone: '', email: '', notes: '' });
  const [submitting, setSubmitting] = useState(false);
  const [confirmation, setConfirmation] = useState(null);

  // My Bookings state
  const [showMyBookings, setShowMyBookings] = useState(false);
  const [lookupField, setLookupField] = useState('phone');
  const [lookupValue, setLookupValue] = useState('');
  const [myBookings, setMyBookings] = useState(null);
  const [lookupLoading, setLookupLoading] = useState(false);
  const [cancellingId, setCancellingId] = useState(null);
  const [otpSent, setOtpSent] = useState(false);
  const [otpValue, setOtpValue] = useState('');
  const [otpVerifying, setOtpVerifying] = useState(false);
  const [otpSentVia, setOtpSentVia] = useState('');

  // Calendar state
  const [calMonth, setCalMonth] = useState(new Date().getMonth());
  const [calYear, setCalYear] = useState(new Date().getFullYear());
  // Week view offset (which week to show)
  const [weekStart, setWeekStart] = useState(null);

  useEffect(() => {
    bookingAPI.getConfig(merchantId)
      .then(({ data }) => { setConfig(data.data); initWeek(); })
      .catch(() => setError('Booking page not available'))
      .finally(() => setLoading(false));
  }, [merchantId]);

  const initWeek = () => {
    const today = new Date();
    const day = today.getDay();
    const start = new Date(today);
    start.setDate(start.getDate() - day);
    start.setHours(0, 0, 0, 0);
    setWeekStart(start);
  };

  useEffect(() => {
    if (step === 3 && weekStart && config) fetchWeekSlots();
  }, [step, weekStart, selectedProvider]);

  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const todayStr = today.toISOString().split('T')[0];
  const maxDate = new Date();
  maxDate.setDate(maxDate.getDate() + (config?.maxAdvanceDays || 30));

  const workingDays = config?.workingDays || [1, 2, 3, 4, 5];

  const isDateSelectable = (d) => {
    const dateStr = toDateStr(d);
    if (dateStr < todayStr) return false;
    if (dateStr > maxDate.toISOString().split('T')[0]) return false;
    return workingDays.includes(d.getDay());
  };

  const toDateStr = (d) => {
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${y}-${m}-${day}`;
  };

  // Get 7 days of the current week
  const weekDays = useMemo(() => {
    if (!weekStart) return [];
    return Array.from({ length: 7 }, (_, i) => {
      const d = new Date(weekStart);
      d.setDate(d.getDate() + i);
      return d;
    });
  }, [weekStart]);

  const fetchWeekSlots = async () => {
    if (!weekStart) return;
    setLoadingSlots(true);
    const newSlots = {};
    const promises = weekDays.map(async (d) => {
      const dateStr = toDateStr(d);
      if (!isDateSelectable(d)) { newSlots[dateStr] = []; return; }
      try {
        const { data } = await bookingAPI.getSlots(merchantId, { date: dateStr, provider: selectedProvider });
        newSlots[dateStr] = (data.data.slots || []).filter(s => s.available);
      } catch (_) { newSlots[dateStr] = []; }
    });
    await Promise.all(promises);
    setSlots(newSlots);
    setLoadingSlots(false);
  };

  const nowMonth = new Date().getMonth();
  const nowYear = new Date().getFullYear();
  const canGoPrevWeek = (() => {
    if (!weekStart) return false;
    const s = new Date(weekStart);
    s.setDate(s.getDate() - 7);
    const thisWeekStart = new Date(today);
    thisWeekStart.setDate(thisWeekStart.getDate() - thisWeekStart.getDay());
    thisWeekStart.setHours(0, 0, 0, 0);
    return s >= thisWeekStart;
  })();
  const canGoPrevMonth = calYear > nowYear || (calYear === nowYear && calMonth > nowMonth);

  const prevWeek = () => {
    if (!canGoPrevWeek) return;
    const s = new Date(weekStart);
    s.setDate(s.getDate() - 7);
    setWeekStart(s);
  };

  const nextWeek = () => {
    const s = new Date(weekStart);
    s.setDate(s.getDate() + 7);
    if (s <= maxDate) setWeekStart(s);
  };

  const calPrev = () => {
    if (!canGoPrevMonth) return;
    if (calMonth === 0) { setCalMonth(11); setCalYear(y => y - 1); }
    else setCalMonth(m => m - 1);
  };

  const calNext = () => {
    if (calMonth === 11) { setCalMonth(0); setCalYear(y => y + 1); }
    else setCalMonth(m => m + 1);
  };

  const handleCalDateClick = (day) => {
    const d = new Date(calYear, calMonth, day);
    if (!isDateSelectable(d)) return;
    // Set week that contains this date
    const dayOfWeek = d.getDay();
    const start = new Date(d);
    start.setDate(start.getDate() - dayOfWeek);
    start.setHours(0, 0, 0, 0);
    setWeekStart(start);
    setSelectedDate(toDateStr(d));
    setSelectedTime('');
  };

  const handleTimeClick = (dateStr, time) => {
    setSelectedDate(dateStr);
    setSelectedTime(time);
  };

  const availableProviders = config?.providers?.filter(p =>
    !p.services?.length || p.services.includes(selectedService?.name)
  ) || [];

  // Full provider object for selected provider
  const selectedProviderObj = selectedProvider ? config?.providers?.find(p => p.name === selectedProvider) : null;
  const providerDisplayName = selectedProviderObj ? `${selectedProviderObj.title ? selectedProviderObj.title + ' ' : ''}${selectedProviderObj.name}` : '';

  const handleBook = async () => {
    if (!form.name || !form.phone) return toast.error('Name and phone are required');
    setSubmitting(true);
    try {
      const { data } = await bookingAPI.book(merchantId, {
        service: selectedService.name,
        provider: selectedProvider,
        date: selectedDate,
        timeSlot: selectedTime,
        customerName: form.name,
        customerPhone: form.phone,
        customerEmail: form.email,
        notes: form.notes,
        ref: sourceRef,
      });
      setConfirmation(data);
      setStep(3); // close form, go back to calendar — confirmation modal shows on top
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to book');
    }
    finally { setSubmitting(false); }
  };

  const handleSendOtp = async () => {
    if (!lookupValue.trim()) return toast.error(`Enter your ${lookupField}`);
    setLookupLoading(true);
    try {
      const body = lookupField === 'phone' ? { phone: lookupValue.trim() } : { email: lookupValue.trim() };
      const { data } = await bookingAPI.sendOtp(merchantId, body);
      setOtpSent(true);
      setOtpValue('');
      setOtpSentVia(data.sentVia || '');
      toast.success(data.message || 'Verification code sent!');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Could not find bookings');
    }
    finally { setLookupLoading(false); }
  };

  const handleVerifyOtp = async () => {
    if (!otpValue.trim() || otpValue.length < 4) return toast.error('Enter the 4-digit code');
    setOtpVerifying(true);
    try {
      const body = { otp: otpValue.trim() };
      if (lookupField === 'phone') body.phone = lookupValue.trim();
      else body.email = lookupValue.trim();
      const { data } = await bookingAPI.verifyOtp(merchantId, body);
      setMyBookings(data);
      toast.success('Verified!');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Invalid code');
    }
    finally { setOtpVerifying(false); }
  };

  const handleCancel = async (appointmentId) => {
    if (!window.confirm('Are you sure you want to cancel this booking?')) return;
    setCancellingId(appointmentId);
    try {
      const body = lookupField === 'phone' ? { phone: lookupValue.trim() } : { email: lookupValue.trim() };
      await bookingAPI.cancel(merchantId, appointmentId, body);
      toast.success('Booking cancelled');
      // Update local state
      setMyBookings(prev => prev ? {
        ...prev,
        data: prev.data.map(b => b.id === appointmentId ? { ...b, status: 'cancelled' } : b),
      } : null);
    } catch (err) { toast.error(err.response?.data?.message || 'Failed to cancel'); }
    finally { setCancellingId(null); }
  };

  const resetMyBookings = () => { setShowMyBookings(false); setMyBookings(null); setLookupValue(''); setOtpSent(false); setOtpValue(''); };

  const renderMyBookingsModal = () => showMyBookings ? (
    <div style={S.overlay} onClick={resetMyBookings}>
      <div style={{ ...S.modal, maxWidth: 500 }} onClick={e => e.stopPropagation()}>
        <div style={S.modalHeader}>
          <h3 style={{ margin: 0, fontSize: '1rem', fontWeight: 700, color: '#1a1a1a', display: 'flex', alignItems: 'center', gap: 8 }}>
            <CalendarDays size={18} /> My Bookings
          </h3>
          <button style={S.modalClose} onClick={resetMyBookings}><X size={20} /></button>
        </div>
        <div style={S.modalBody}>

          {/* Step 1: Enter phone/email */}
          {!otpSent && !myBookings && (
            <div>
              <div style={{ fontSize: '0.85rem', color: '#5f6368', marginBottom: 10 }}>Enter your phone or email to look up bookings</div>
              <div style={{ display: 'flex', gap: 8, marginBottom: 10 }}>
                <button onClick={() => { setLookupField('phone'); setOtpSent(false); }}
                  style={{ flex: 1, padding: '8px', borderRadius: 8, border: `1px solid ${lookupField === 'phone' ? '#1a73e8' : '#dadce0'}`, background: lookupField === 'phone' ? '#e8f0fe' : '#fff', color: lookupField === 'phone' ? '#1a73e8' : '#5f6368', fontWeight: 600, fontSize: '0.82rem', cursor: 'pointer' }}>
                  <Phone size={13} style={{ verticalAlign: -2, marginRight: 4 }} /> Phone
                </button>
                <button onClick={() => { setLookupField('email'); setOtpSent(false); }}
                  style={{ flex: 1, padding: '8px', borderRadius: 8, border: `1px solid ${lookupField === 'email' ? '#1a73e8' : '#dadce0'}`, background: lookupField === 'email' ? '#e8f0fe' : '#fff', color: lookupField === 'email' ? '#1a73e8' : '#5f6368', fontWeight: 600, fontSize: '0.82rem', cursor: 'pointer' }}>
                  <Mail size={13} style={{ verticalAlign: -2, marginRight: 4 }} /> Email
                </button>
              </div>
              <div style={{ display: 'flex', gap: 8 }}>
                <input type={lookupField === 'email' ? 'email' : 'tel'} value={lookupValue}
                  onChange={e => setLookupValue(e.target.value)}
                  onKeyDown={e => e.key === 'Enter' && handleSendOtp()}
                  placeholder={lookupField === 'phone' ? '+1234567890' : 'your@email.com'}
                  style={{ ...S.formInput, flex: 1 }} />
                <button onClick={handleSendOtp} disabled={lookupLoading}
                  style={{ padding: '10px 18px', borderRadius: 10, border: 'none', background: '#1a73e8', color: '#fff', fontWeight: 600, fontSize: '0.88rem', cursor: lookupLoading ? 'wait' : 'pointer', whiteSpace: 'nowrap' }}>
                  {lookupLoading ? '...' : 'Send Code'}
                </button>
              </div>
            </div>
          )}

          {/* Step 2: Enter OTP */}
          {otpSent && !myBookings && (
            <div>
              <div style={{ textAlign: 'center', marginBottom: 16 }}>
                <div style={{ width: 48, height: 48, borderRadius: '50%', background: '#e8f0fe', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 12px' }}>
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#1a73e8" strokeWidth="2" strokeLinecap="round"><rect x="3" y="11" width="18" height="11" rx="2" ry="2"/><path d="M7 11V7a5 5 0 0110 0v4"/></svg>
                </div>
                <div style={{ fontWeight: 600, fontSize: '0.95rem', color: '#1a1a1a' }}>Enter verification code</div>
                <div style={{ fontSize: '0.82rem', color: '#5f6368', marginTop: 4 }}>
                  We sent a 4-digit code to <strong>{lookupValue}</strong>
                  {otpSentVia && otpSentVia !== 'console' && <span> via <strong style={{ textTransform: 'capitalize' }}>{otpSentVia}</strong></span>}
                </div>
              </div>
              <div style={{ display: 'flex', justifyContent: 'center', gap: 8, marginBottom: 16 }}>
                {[0, 1, 2, 3].map(i => (
                  <input key={i} type="text" maxLength={1} value={otpValue[i] || ''}
                    style={{ width: 48, height: 56, textAlign: 'center', fontSize: '1.4rem', fontWeight: 700, borderRadius: 10, border: '2px solid #dadce0', outline: 'none', color: '#1a1a1a' }}
                    onFocus={e => e.target.style.borderColor = '#1a73e8'}
                    onBlur={e => e.target.style.borderColor = '#dadce0'}
                    onChange={e => {
                      const val = e.target.value.replace(/\D/g, '');
                      if (!val && otpValue.length > i) {
                        setOtpValue(prev => prev.slice(0, i) + prev.slice(i + 1));
                        return;
                      }
                      const newOtp = otpValue.padEnd(i, ' ').split('');
                      newOtp[i] = val;
                      const joined = newOtp.join('').replace(/ /g, '').slice(0, 4);
                      setOtpValue(joined);
                      if (val && e.target.nextElementSibling) e.target.nextElementSibling.focus();
                    }}
                    onKeyDown={e => {
                      if (e.key === 'Backspace' && !otpValue[i] && e.target.previousElementSibling) e.target.previousElementSibling.focus();
                    }}
                  />
                ))}
              </div>
              <button onClick={handleVerifyOtp} disabled={otpVerifying || otpValue.length < 4}
                style={{ width: '100%', padding: '12px', borderRadius: 10, border: 'none', background: '#1a73e8', color: '#fff', fontWeight: 700, fontSize: '0.92rem', cursor: otpVerifying ? 'wait' : 'pointer', opacity: otpValue.length < 4 ? 0.6 : 1 }}>
                {otpVerifying ? 'Verifying...' : 'Verify'}
              </button>
              <div style={{ textAlign: 'center', marginTop: 12 }}>
                <button onClick={() => { setOtpSent(false); setOtpValue(''); }} style={{ background: 'none', border: 'none', color: '#1a73e8', fontSize: '0.82rem', fontWeight: 500, cursor: 'pointer' }}>
                  &larr; Change {lookupField}
                </button>
                <span style={{ color: '#dadce0', margin: '0 8px' }}>|</span>
                <button onClick={handleSendOtp} disabled={lookupLoading} style={{ background: 'none', border: 'none', color: '#1a73e8', fontSize: '0.82rem', fontWeight: 500, cursor: 'pointer' }}>
                  Resend code
                </button>
              </div>
            </div>
          )}

          {/* Step 3: Show bookings (after OTP verified) */}
          {myBookings && (
            <div>
              {myBookings.data.length === 0 ? (
                <div style={{ textAlign: 'center', padding: '24px 0', color: '#70757a' }}>
                  <CalendarDays size={32} style={{ color: '#dadce0', marginBottom: 8 }} />
                  <div style={{ fontSize: '0.9rem' }}>No bookings found</div>
                </div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                  {myBookings.data.map(b => {
                    const bookDate = new Date(b.date + 'T00:00:00');
                    const dayName = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'][bookDate.getDay()];
                    const isPast = b.date < todayStr;
                    const isCancelled = b.status === 'cancelled';
                    return (
                      <div key={b.id} style={{ border: '1px solid #e5e7eb', borderRadius: 12, padding: '14px 16px', background: isCancelled ? '#fafafa' : '#fff', opacity: isCancelled ? 0.7 : 1 }}>
                        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 14 }}>
                          <div style={{ textAlign: 'center', minWidth: 40, paddingTop: 2 }}>
                            <div style={{ fontSize: '1.2rem', fontWeight: 600, color: isCancelled ? '#b0b0b0' : '#1a1a1a', lineHeight: 1 }}>{bookDate.getDate()}</div>
                            <div style={{ fontSize: '0.7rem', fontWeight: 500, color: '#70757a' }}>{MONTHS_SHORT[bookDate.getMonth()].toUpperCase()}</div>
                          </div>
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <div style={{ fontWeight: 600, fontSize: '0.92rem', color: '#1a1a1a' }}>{b.service}</div>
                            <div style={{ fontSize: '0.82rem', color: '#5f6368', marginTop: 2 }}>{dayName} &middot; {b.timeSlot}{b.provider ? ` with ${b.provider}` : ''}</div>
                            {b.bookingId && <div style={{ fontSize: '0.72rem', color: '#94a3b8', marginTop: 2 }}>ID: {b.bookingId}</div>}
                            {b.price > 0 && <div style={{ fontSize: '0.82rem', color: '#5f6368' }}>{myBookings.currency} {b.price}</div>}
                            <div style={{ marginTop: 6 }}>
                              <span style={{
                                display: 'inline-block', padding: '2px 8px', borderRadius: 6, fontSize: '0.7rem', fontWeight: 600,
                                background: isCancelled ? '#fee2e2' : b.status === 'completed' ? '#e8f5e9' : isPast ? '#f3f4f6' : '#e8f0fe',
                                color: isCancelled ? '#dc2626' : b.status === 'completed' ? '#16a34a' : isPast ? '#6b7280' : '#1a73e8',
                              }}>
                                {isCancelled ? 'Cancelled' : b.status === 'completed' ? 'Completed' : isPast ? 'Past' : b.status === 'confirmed' ? 'Confirmed' : b.status}
                              </span>
                            </div>
                          </div>
                          {!isCancelled && !isPast && b.status !== 'completed' && (
                            <button onClick={() => handleCancel(b.id)} disabled={cancellingId === b.id}
                              style={{ background: 'none', border: '1px solid #fca5a5', borderRadius: 8, padding: '6px 12px', fontSize: '0.78rem', fontWeight: 600, color: '#dc2626', cursor: cancellingId === b.id ? 'wait' : 'pointer', whiteSpace: 'nowrap', display: 'flex', alignItems: 'center', gap: 4 }}>
                              <XCircle size={13} /> {cancellingId === b.id ? '...' : 'Cancel'}
                            </button>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  ) : null;

  const getDaysInMonth = (m, y) => new Date(y, m + 1, 0).getDate();
  const getFirstDayOfMonth = (m, y) => new Date(y, m, 1).getDay();

  const formatDate = (dateStr) => {
    const d = new Date(dateStr + 'T00:00:00');
    return `${DAYS_FULL[d.getDay()]}, ${d.getDate()} ${MONTHS[d.getMonth()]} ${d.getFullYear()}`;
  };

  const formatTimeRange = (time, duration) => {
    const [h, m] = time.split(':').map(Number);
    const startH = h > 12 ? h - 12 : h === 0 ? 12 : h;
    const startSuffix = h >= 12 ? 'pm' : 'am';
    const endMin = m + (duration || 30);
    let endH = h + Math.floor(endMin / 60);
    const endM = endMin % 60;
    const endSuffix = endH >= 12 ? 'pm' : 'am';
    if (endH > 12) endH -= 12;
    if (endH === 0) endH = 12;
    return `${startH}:${String(m).padStart(2, '0')}${startSuffix} – ${endH}:${String(endM).padStart(2, '0')}${endSuffix}`;
  };

  // Styles
  const S = {
    page: { minHeight: '100vh', background: '#fff', fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif" },
    container: { maxWidth: 1100, margin: '0 auto', padding: '0 16px' },
    // Header
    header: { borderBottom: '1px solid #e5e7eb', padding: '24px 0' },
    headerInner: { maxWidth: 1100, margin: '0 auto', padding: '0 16px' },
    businessName: { fontSize: '1.35rem', fontWeight: 700, color: '#1a1a1a', margin: 0 },
    headerMeta: { display: 'flex', flexWrap: 'wrap', gap: '16px', marginTop: 10, fontSize: '0.88rem', color: '#5f6368', alignItems: 'flex-start' },
    metaItem: { display: 'flex', alignItems: 'flex-start', gap: 6 },
    // Calendar layout
    calendarLayout: { display: 'flex', gap: 0, minHeight: 480 },
    // Mini calendar sidebar
    miniCal: { width: 260, flexShrink: 0, padding: '24px 24px 24px 0', borderRight: '1px solid #e5e7eb' },
    miniCalHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 },
    miniCalTitle: { fontWeight: 600, fontSize: '0.88rem', color: '#1a1a1a' },
    miniCalNav: { background: 'none', border: 'none', cursor: 'pointer', padding: 4, color: '#5f6368', borderRadius: 4 },
    miniCalGrid: { display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 0, textAlign: 'center' },
    miniCalDay: { fontSize: '0.7rem', fontWeight: 500, color: '#70757a', padding: '4px 0' },
    miniCalDate: (selectable, isSelected, isToday) => ({
      width: 32, height: 32, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center',
      fontSize: '0.78rem', fontWeight: isToday ? 700 : 400, cursor: selectable ? 'pointer' : 'default', margin: '0 auto',
      background: isSelected ? '#1a73e8' : 'transparent',
      color: isSelected ? '#fff' : isToday ? '#1a73e8' : selectable ? '#1a1a1a' : '#dadce0',
    }),
    // Week view
    weekView: { flex: 1, overflow: 'hidden' },
    weekHeader: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '16px 20px', borderBottom: '1px solid #e5e7eb' },
    weekNav: { display: 'flex', alignItems: 'center', gap: 8 },
    weekNavBtn: { background: 'none', border: '1px solid #dadce0', borderRadius: '50%', width: 32, height: 32, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#5f6368' },
    weekLabel: { fontWeight: 600, fontSize: '0.95rem', color: '#1a1a1a' },
    weekGrid: { display: 'flex', overflow: 'hidden' },
    dayCol: { flex: 1, borderRight: '1px solid #f0f0f0', minWidth: 0 },
    dayColLast: { flex: 1, minWidth: 0 },
    dayHeader: (isToday) => ({
      textAlign: 'center', padding: '12px 4px', borderBottom: '1px solid #e5e7eb',
      background: isToday ? '#e8f0fe' : '#fafafa',
    }),
    dayHeaderLabel: (isToday) => ({ fontSize: '0.7rem', fontWeight: 500, color: isToday ? '#1a73e8' : '#70757a', letterSpacing: '0.5px' }),
    dayHeaderNum: (isToday) => ({
      fontSize: '1.5rem', fontWeight: isToday ? 700 : 400,
      color: isToday ? '#1a73e8' : '#1a1a1a', lineHeight: 1.2,
    }),
    daySlots: { padding: '8px 4px', display: 'flex', flexDirection: 'column', gap: 6, alignItems: 'center', maxHeight: 380, overflowY: 'auto' },
    slotBtn: (isSelected) => ({
      width: '90%', maxWidth: 120, padding: '10px 4px', borderRadius: 8, border: `2px solid ${isSelected ? '#1a73e8' : '#dadce0'}`,
      background: isSelected ? '#1a73e8' : '#fff', color: isSelected ? '#fff' : '#1a73e8',
      fontWeight: 600, fontSize: '0.8rem', cursor: 'pointer', textAlign: 'center', transition: '0.15s',
    }),
    noSlot: { color: '#b0b0b0', fontSize: '0.78rem', padding: '16px 0', textAlign: 'center' },
    // Booking form overlay
    overlay: { position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.4)', zIndex: 999, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 },
    modal: { background: '#fff', borderRadius: 16, width: '100%', maxWidth: 440, maxHeight: '90vh', overflowY: 'auto', boxShadow: '0 20px 60px rgba(0,0,0,0.15)' },
    modalHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '16px 20px', borderBottom: '1px solid #e5e7eb' },
    modalClose: { background: 'none', border: 'none', cursor: 'pointer', padding: 4, color: '#5f6368' },
    modalBody: { padding: '20px' },
    formGroup: { marginBottom: 14 },
    formLabel: { display: 'block', fontSize: '0.82rem', fontWeight: 600, color: '#374151', marginBottom: 4 },
    formInput: { width: '100%', padding: '10px 14px', borderRadius: 10, border: '1px solid #dadce0', fontSize: '0.9rem', outline: 'none', boxSizing: 'border-box' },
    confirmBtn: (disabled) => ({
      width: '100%', padding: '14px', borderRadius: 10, border: 'none',
      background: '#1a73e8', color: '#fff', fontWeight: 700, fontSize: '0.95rem',
      cursor: disabled ? 'wait' : 'pointer', opacity: disabled ? 0.7 : 1,
    }),
    // Confirmed modal
    confirmedIcon: { width: 56, height: 56, borderRadius: '50%', background: '#e8f5e9', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 16px' },
    summaryRow: { display: 'flex', justifyContent: 'space-between', padding: '8px 0', borderBottom: '1px solid #f0f0f0', fontSize: '0.88rem' },
    // Mobile
    mobileWeekScroll: { display: 'flex', overflowX: 'auto', WebkitOverflowScrolling: 'touch', scrollbarWidth: 'none' },
  };

  if (loading) return (
    <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div className="spinner" />
    </div>
  );

  if (error) return (
    <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div style={{ textAlign: 'center', padding: 40 }}>
        <AlertCircle size={48} style={{ color: '#94a3b8', marginBottom: 16 }} />
        <h2 style={{ color: '#1e293b' }}>{error}</h2>
      </div>
    </div>
  );

  // Service selection step
  if (step === 1) return (
    <div style={S.page}>
      <div style={S.header}>
        <div style={S.headerInner}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
            <div>
              <h1 style={S.businessName}>{config.businessName}</h1>
              <p style={{ color: '#5f6368', fontSize: '0.9rem', marginTop: 4 }}>Select a service to book</p>
            </div>
            <button onClick={() => setShowMyBookings(true)} style={{ background: 'none', border: '1px solid #dadce0', borderRadius: 8, padding: '8px 14px', fontSize: '0.82rem', fontWeight: 600, color: '#1a73e8', cursor: 'pointer', whiteSpace: 'nowrap', display: 'flex', alignItems: 'center', gap: 6 }}>
              <CalendarDays size={15} /> My Bookings
            </button>
          </div>
        </div>
      </div>
      <div style={{ ...S.container, paddingTop: 24, paddingBottom: 40 }}>
        <div style={{ maxWidth: 560, margin: '0 auto', display: 'flex', flexDirection: 'column', gap: 10 }}>
          {(config.services || []).map((svc, i) => (
            <div key={i} onClick={() => { setSelectedService(svc); setStep(availableProviders.length > 1 ? 2 : 3); if (!weekStart) initWeek(); }}
              style={{ padding: '18px 20px', borderRadius: 12, cursor: 'pointer', border: '1px solid #dadce0', transition: '0.15s', background: '#fff' }}
              onMouseEnter={e => { e.currentTarget.style.borderColor = '#1a73e8'; e.currentTarget.style.boxShadow = '0 1px 6px rgba(26,115,232,0.12)'; }}
              onMouseLeave={e => { e.currentTarget.style.borderColor = '#dadce0'; e.currentTarget.style.boxShadow = 'none'; }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div>
                  <div style={{ fontWeight: 600, fontSize: '0.95rem', color: '#1a1a1a' }}>{svc.name}</div>
                  <div style={{ fontSize: '0.8rem', color: '#70757a', marginTop: 2, display: 'flex', alignItems: 'center', gap: 6 }}>
                    <Clock size={13} /> {svc.duration} min
                  </div>
                </div>
                {svc.price > 0 && <div style={{ fontWeight: 700, fontSize: '1rem', color: '#1a73e8' }}>{config.currency} {svc.price}</div>}
              </div>
            </div>
          ))}
        </div>
      </div>
      <div style={{ textAlign: 'center', padding: '16px 0', fontSize: '0.72rem', color: '#b0b0b0' }}>Powered by AINA</div>
      {renderMyBookingsModal()}
    </div>
  );

  // Provider selection step
  if (step === 2) return (
    <div style={S.page}>
      <div style={S.header}>
        <div style={S.headerInner}>
          <h1 style={S.businessName}>{config.businessName}</h1>
          <div style={S.headerMeta}>
            <span style={{ cursor: 'pointer', color: '#1a73e8', fontWeight: 600, fontSize: '0.85rem' }} onClick={() => setStep(1)}>&larr; Back</span>
            <span>{selectedService?.name} &middot; {selectedService?.duration} min</span>
          </div>
        </div>
      </div>
      <div style={{ ...S.container, paddingTop: 24, paddingBottom: 40 }}>
        <h2 style={{ fontSize: '1rem', fontWeight: 600, color: '#1a1a1a', marginBottom: 16 }}>Select a provider</h2>
        <div style={{ maxWidth: 560, margin: '0 auto', display: 'flex', flexDirection: 'column', gap: 10 }}>
          <div onClick={() => { setSelectedProvider(''); setStep(3); if (!weekStart) initWeek(); }}
            style={{ padding: '16px 20px', borderRadius: 12, cursor: 'pointer', border: '1px solid #dadce0', background: '#fff' }}
            onMouseEnter={e => e.currentTarget.style.borderColor = '#1a73e8'}
            onMouseLeave={e => e.currentTarget.style.borderColor = '#dadce0'}>
            <div style={{ fontWeight: 600, color: '#1a1a1a' }}>Any Available</div>
            <div style={{ fontSize: '0.78rem', color: '#70757a' }}>First available provider</div>
          </div>
          {availableProviders.map((p, i) => (
            <div key={i} onClick={() => { setSelectedProvider(p.name); setStep(3); if (!weekStart) initWeek(); }}
              style={{ padding: '16px 20px', borderRadius: 12, cursor: 'pointer', border: '1px solid #dadce0', background: '#fff' }}
              onMouseEnter={e => e.currentTarget.style.borderColor = '#1a73e8'}
              onMouseLeave={e => e.currentTarget.style.borderColor = '#dadce0'}>
              <div style={{ fontWeight: 600, color: '#1a1a1a' }}>{p.title ? `${p.title} ` : ''}{p.name}</div>
            </div>
          ))}
        </div>
      </div>
      {renderMyBookingsModal()}
    </div>
  );

  // Calendar view (main step)
  const weekNum = weekStart ? Math.ceil(((weekStart - new Date(weekStart.getFullYear(), 0, 1)) / 86400000 + 1) / 7) : 1;

  return (
    <div style={S.page}>
      {/* Header */}
      <div style={S.header}>
        <div style={S.headerInner}>
          <div style={{ display: 'flex', alignItems: 'flex-start', gap: 16, flexWrap: 'wrap' }}>
            <div style={{ flex: 1, minWidth: 200 }}>
              <h1 style={S.businessName}>{config.businessName}</h1>
              <div style={S.headerMeta}>
                <div style={S.metaItem}><Clock size={16} style={{ marginTop: 1 }} /> {selectedService?.duration} min appointments</div>
                <div style={{ color: '#1a1a1a', fontWeight: 500 }}>{selectedService?.name}</div>
                {providerDisplayName && <div style={S.metaItem}><User size={15} style={{ marginTop: 1 }} /> {providerDisplayName}</div>}
              </div>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8, alignItems: 'flex-end', paddingTop: 4 }}>
              <span style={{ cursor: 'pointer', color: '#1a73e8', fontWeight: 600, fontSize: '0.85rem' }}
                onClick={() => setStep(availableProviders.length > 1 ? 2 : 1)}>&larr; Change service</span>
              <button onClick={() => setShowMyBookings(true)} style={{ background: 'none', border: '1px solid #dadce0', borderRadius: 8, padding: '6px 12px', fontSize: '0.78rem', fontWeight: 600, color: '#1a73e8', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 4 }}>
                <CalendarDays size={13} /> My Bookings
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Title */}
      <div style={{ ...S.container, padding: '20px 16px 0' }}>
        <h2 style={{ fontSize: '1.05rem', fontWeight: 600, color: '#1a1a1a', margin: 0 }}>Select an appointment time</h2>
        {providerDisplayName && <p style={{ fontSize: '0.85rem', color: '#5f6368', margin: '4px 0 0' }}>with {providerDisplayName}</p>}
      </div>

      {/* Calendar + Week View */}
      <div style={S.container}>
        {/* Desktop layout */}
        <div className="booking-cal-desktop" style={{ ...S.calendarLayout }}>
          {/* Mini Calendar */}
          <div className="booking-mini-cal" style={S.miniCal}>
            <div style={{ fontSize: '0.82rem', fontWeight: 600, color: '#1a1a1a', marginBottom: 16 }}>
              {MONTHS[calMonth]} {calYear}
            </div>
            <div style={S.miniCalHeader}>
              <button style={{ ...S.miniCalNav, opacity: canGoPrevMonth ? 1 : 0.3, cursor: canGoPrevMonth ? 'pointer' : 'default' }} onClick={calPrev} disabled={!canGoPrevMonth}><ChevronLeft size={16} /></button>
              <button style={S.miniCalNav} onClick={calNext}><ChevronRight size={16} /></button>
            </div>
            <div style={S.miniCalGrid}>
              {DAYS_SHORT.map((d, i) => <div key={i} style={S.miniCalDay}>{d}</div>)}
              {Array.from({ length: getFirstDayOfMonth(calMonth, calYear) }).map((_, i) => <div key={`e${i}`} />)}
              {Array.from({ length: getDaysInMonth(calMonth, calYear) }).map((_, i) => {
                const day = i + 1;
                const d = new Date(calYear, calMonth, day);
                const dateStr = toDateStr(d);
                const selectable = isDateSelectable(d);
                const isSelected = selectedDate === dateStr;
                const isTodayDate = dateStr === todayStr;
                return (
                  <div key={day} onClick={() => selectable && handleCalDateClick(day)} style={{ padding: '2px 0' }}>
                    <div style={S.miniCalDate(selectable, isSelected, isTodayDate)}>{day}</div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Week Slots */}
          <div style={S.weekView}>
            <div style={S.weekHeader}>
              <div style={S.weekNav}>
                <button style={{ ...S.weekNavBtn, opacity: canGoPrevWeek ? 1 : 0.3, cursor: canGoPrevWeek ? 'pointer' : 'default' }} onClick={prevWeek} disabled={!canGoPrevWeek}><ChevronLeft size={16} /></button>
                <button style={S.weekNavBtn} onClick={nextWeek}><ChevronRight size={16} /></button>
              </div>
            </div>

            {loadingSlots ? (
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 60 }}>
                <div className="spinner" />
              </div>
            ) : (
              <div className="booking-week-grid" style={S.weekGrid}>
                {weekDays.map((d, i) => {
                  const dateStr = toDateStr(d);
                  const daySlots = slots[dateStr] || [];
                  const selectable = isDateSelectable(d);
                  const isTodayDate = dateStr === todayStr;
                  return (
                    <div key={i} style={i < 6 ? S.dayCol : S.dayColLast}>
                      <div style={S.dayHeader(isTodayDate)}>
                        <div style={S.dayHeaderLabel(isTodayDate)}>{DAYS_FULL[d.getDay()]}</div>
                        <div style={S.dayHeaderNum(isTodayDate)}>{d.getDate()}</div>
                      </div>
                      <div style={S.daySlots}>
                        {!selectable ? (
                          <div style={S.noSlot}>&mdash;</div>
                        ) : daySlots.length === 0 ? (
                          <div style={S.noSlot}>&mdash;</div>
                        ) : daySlots.map(slot => (
                          <button key={slot.time} style={S.slotBtn(selectedDate === dateStr && selectedTime === slot.time)}
                            onClick={() => handleTimeClick(dateStr, slot.time)}>
                            {slot.time}
                          </button>
                        ))}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>

        {/* Mobile: horizontal scroll week */}
        <div className="booking-cal-mobile" style={{ display: 'none' }}>
          {/* Mini calendar */}
          <div style={{ padding: '16px 0' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
              <span style={{ fontWeight: 600, fontSize: '0.88rem', color: '#1a1a1a' }}>{MONTHS[calMonth]} {calYear}</span>
              <div style={{ display: 'flex', gap: 4 }}>
                <button style={{ ...S.miniCalNav, opacity: canGoPrevMonth ? 1 : 0.3, cursor: canGoPrevMonth ? 'pointer' : 'default' }} onClick={calPrev} disabled={!canGoPrevMonth}><ChevronLeft size={16} /></button>
                <button style={S.miniCalNav} onClick={calNext}><ChevronRight size={16} /></button>
              </div>
            </div>
            <div style={S.miniCalGrid}>
              {DAYS_SHORT.map((d, i) => <div key={i} style={S.miniCalDay}>{d}</div>)}
              {Array.from({ length: getFirstDayOfMonth(calMonth, calYear) }).map((_, i) => <div key={`e${i}`} />)}
              {Array.from({ length: getDaysInMonth(calMonth, calYear) }).map((_, i) => {
                const day = i + 1;
                const d = new Date(calYear, calMonth, day);
                const dateStr = toDateStr(d);
                const selectable = isDateSelectable(d);
                const isSelected = selectedDate === dateStr;
                const isTodayDate = dateStr === todayStr;
                return (
                  <div key={day} onClick={() => selectable && handleCalDateClick(day)} style={{ padding: '2px 0' }}>
                    <div style={S.miniCalDate(selectable, isSelected, isTodayDate)}>{day}</div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Horizontal scrollable time slots */}
          <div style={{ padding: '8px 0', borderTop: '1px solid #e5e7eb' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '8px 0' }}>
              <button style={{ ...S.weekNavBtn, opacity: canGoPrevWeek ? 1 : 0.3, cursor: canGoPrevWeek ? 'pointer' : 'default' }} onClick={prevWeek} disabled={!canGoPrevWeek}><ChevronLeft size={16} /></button>
              <button style={S.weekNavBtn} onClick={nextWeek}><ChevronRight size={16} /></button>
            </div>
            {loadingSlots ? (
              <div style={{ textAlign: 'center', padding: 30 }}><div className="spinner" /></div>
            ) : (
              <div style={S.mobileWeekScroll}>
                {weekDays.map((d, i) => {
                  const dateStr = toDateStr(d);
                  const daySlots = slots[dateStr] || [];
                  const selectable = isDateSelectable(d);
                  const isTodayDate = dateStr === todayStr;
                  return (
                    <div key={i} style={{ minWidth: 100, flex: '0 0 auto', borderRight: i < 6 ? '1px solid #f0f0f0' : 'none', padding: '0 2px' }}>
                      <div style={S.dayHeader(isTodayDate)}>
                        <div style={S.dayHeaderLabel(isTodayDate)}>{DAYS_FULL[d.getDay()]}</div>
                        <div style={S.dayHeaderNum(isTodayDate)}>{d.getDate()}</div>
                      </div>
                      <div style={{ padding: '8px 4px', display: 'flex', flexDirection: 'column', gap: 6, alignItems: 'center' }}>
                        {!selectable || daySlots.length === 0 ? (
                          <div style={S.noSlot}>&mdash;</div>
                        ) : daySlots.map(slot => (
                          <button key={slot.time} style={S.slotBtn(selectedDate === dateStr && selectedTime === slot.time)}
                            onClick={() => handleTimeClick(dateStr, slot.time)}>
                            {slot.time}
                          </button>
                        ))}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Continue bar */}
      {selectedTime && !confirmation && (
        <div style={{ position: 'fixed', bottom: 0, left: 0, right: 0, background: '#fff', borderTop: '1px solid #e5e7eb', padding: '12px 16px', zIndex: 10, boxShadow: '0 -2px 10px rgba(0,0,0,0.06)' }}>
          <div style={{ maxWidth: 1100, margin: '0 auto', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
            <div style={{ fontSize: '0.88rem', color: '#1a1a1a' }}>
              <strong>{selectedService?.name}</strong>{providerDisplayName ? ` with ${providerDisplayName}` : ''} &middot; {formatDate(selectedDate)} at <strong>{selectedTime}</strong>
            </div>
            <button onClick={() => setStep(4)}
              style={{ padding: '10px 28px', borderRadius: 8, border: 'none', background: '#1a73e8', color: '#fff', fontWeight: 700, fontSize: '0.9rem', cursor: 'pointer', whiteSpace: 'nowrap' }}>
              Continue
            </button>
          </div>
        </div>
      )}

      {/* Step 4: Details Form Modal */}
      {step === 4 && (
        <div style={S.overlay} onClick={() => setStep(3)}>
          <div style={S.modal} onClick={e => e.stopPropagation()}>
            <div style={S.modalHeader}>
              <h3 style={{ margin: 0, fontSize: '1rem', fontWeight: 700, color: '#1a1a1a' }}>Your Details</h3>
              <button style={S.modalClose} onClick={() => setStep(3)}><X size={20} /></button>
            </div>
            <div style={S.modalBody}>
              {/* Summary */}
              <div style={{ padding: '12px 14px', background: '#f8f9fa', borderRadius: 10, marginBottom: 20, border: '1px solid #e5e7eb' }}>
                <div style={{ fontSize: '0.85rem', color: '#5f6368', display: 'flex', flexDirection: 'column', gap: 4 }}>
                  <div><strong>{selectedService?.name}</strong></div>
                  {providerDisplayName && <div>with {providerDisplayName}</div>}
                  <div>{formatDate(selectedDate)} &middot; {selectedTime}</div>
                  {selectedService?.price > 0 && <div style={{ color: '#1a73e8', fontWeight: 600 }}>{config.currency} {selectedService.price}</div>}
                </div>
              </div>

              <div style={S.formGroup}>
                <label style={S.formLabel}><User size={13} style={{ verticalAlign: -2, marginRight: 4 }} />Full Name *</label>
                <input type="text" value={form.name} onChange={e => setForm(p => ({ ...p, name: e.target.value }))}
                  placeholder="John Doe" style={S.formInput} />
              </div>
              <div style={S.formGroup}>
                <label style={S.formLabel}><Phone size={13} style={{ verticalAlign: -2, marginRight: 4 }} />Phone *</label>
                <input type="tel" value={form.phone} onChange={e => setForm(p => ({ ...p, phone: e.target.value }))}
                  placeholder="+1234567890" style={S.formInput} />
              </div>
              <div style={S.formGroup}>
                <label style={S.formLabel}><Mail size={13} style={{ verticalAlign: -2, marginRight: 4 }} />Email (optional)</label>
                <input type="email" value={form.email} onChange={e => setForm(p => ({ ...p, email: e.target.value }))}
                  placeholder="john@example.com" style={S.formInput} />
              </div>
              <div style={S.formGroup}>
                <label style={S.formLabel}>Notes (optional)</label>
                <textarea value={form.notes} onChange={e => setForm(p => ({ ...p, notes: e.target.value }))}
                  placeholder="Any special requests..." rows={2}
                  style={{ ...S.formInput, resize: 'vertical' }} />
              </div>

              {config.cancellationPolicy && (
                <div style={{ marginBottom: 14, padding: '8px 12px', background: '#fef3c7', borderRadius: 8, fontSize: '0.75rem', color: '#92400e' }}>
                  <strong>Cancellation Policy:</strong> {config.cancellationPolicy}
                </div>
              )}

              <button onClick={handleBook} disabled={submitting} style={S.confirmBtn(submitting)}>
                {submitting ? 'Booking...' : 'Confirm Booking'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Confirmation Modal */}
      {confirmation && (() => {
        const bookDate = new Date(confirmation.data.date + 'T00:00:00');
        const dayName = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'][bookDate.getDay()];
        return (
          <div style={S.overlay} onClick={() => setConfirmation(null)}>
            <div style={{ ...S.modal, maxWidth: 460 }} onClick={e => e.stopPropagation()}>
              <div style={{ padding: '32px 28px 24px', textAlign: 'center' }}>
                {/* Checkmark */}
                <div style={{ width: 64, height: 64, borderRadius: '50%', border: '3px solid #1a73e8', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 20px' }}>
                  <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="#1a73e8" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12" /></svg>
                </div>
                <h2 style={{ fontSize: '1.35rem', fontWeight: 400, color: '#1a1a1a', margin: '0 0 6px' }}>Booking confirmed</h2>
                {confirmation.data.bookingId && (
                  <div style={{ margin: '8px auto 4px', padding: '6px 16px', background: '#f0f4f8', borderRadius: 8, display: 'inline-block' }}>
                    <span style={{ fontSize: '0.75rem', color: '#5f6368' }}>Booking ID: </span>
                    <span style={{ fontSize: '0.88rem', fontWeight: 700, color: '#1a1a1a', letterSpacing: '0.5px' }}>{confirmation.data.bookingId}</span>
                  </div>
                )}
                {form.email && <p style={{ color: '#5f6368', fontSize: '0.88rem', margin: '6px 0 0' }}>Confirmation sent to<br /><span style={{ color: '#1a1a1a' }}>{form.email}</span></p>}
              </div>

              <div style={{ borderTop: '1px solid #e5e7eb', padding: '20px 28px' }}>
                {/* Date + business info */}
                <div style={{ display: 'flex', alignItems: 'flex-start', gap: 16, marginBottom: 20 }}>
                  <div style={{ textAlign: 'center', minWidth: 40, paddingTop: 2 }}>
                    <div style={{ fontSize: '1.4rem', fontWeight: 400, color: '#1a1a1a', lineHeight: 1 }}>{bookDate.getDate()}</div>
                    <div style={{ fontSize: '0.72rem', fontWeight: 500, color: '#70757a', marginTop: 2 }}>{MONTHS_SHORT[bookDate.getMonth()].toUpperCase()}</div>
                  </div>
                  <div>
                    <div style={{ fontWeight: 600, fontSize: '0.95rem', color: '#1a1a1a' }}>{config.businessName}</div>
                    <div style={{ fontSize: '0.85rem', color: '#5f6368', marginTop: 3 }}>
                      {dayName} &middot; {confirmation.data.timeSlot}
                    </div>
                    <div style={{ fontSize: '0.85rem', color: '#5f6368', marginTop: 1 }}>
                      {confirmation.data.service}{providerDisplayName ? ` with ${providerDisplayName}` : ''}
                    </div>
                  </div>
                </div>
              </div>

              {/* Add to Calendar — smart single button based on device */}
              <div style={{ borderTop: '1px solid #e5e7eb', padding: '16px 28px' }}>
                {(() => {
                  const [sH, sM] = (confirmation.data.timeSlot || '00:00').split(':').map(Number);
                  const dur = selectedService?.duration || 30;
                  const startDt = new Date(bookDate.getFullYear(), bookDate.getMonth(), bookDate.getDate(), sH, sM);
                  const endDt = new Date(startDt.getTime() + dur * 60000);
                  const fmt = (d) => d.toISOString().replace(/[-:]/g, '').replace(/\.\d+/, '');
                  const title = encodeURIComponent(`${confirmation.data.service} - ${config.businessName}`);
                  const details = encodeURIComponent(`Service: ${confirmation.data.service}${providerDisplayName ? `\nProvider: ${providerDisplayName}` : ''}\nBooked at: ${config.businessName}`);
                  const googleUrl = `https://calendar.google.com/calendar/render?action=TEMPLATE&text=${title}&dates=${fmt(startDt)}/${fmt(endDt)}&details=${details}`;
                  const outlookUrl = `https://outlook.live.com/calendar/0/action/compose?subject=${title}&startdt=${startDt.toISOString()}&enddt=${endDt.toISOString()}&body=${details}`;
                  const icsContent = `BEGIN:VCALENDAR\nVERSION:2.0\nPRODID:-//AINA//Booking//EN\nBEGIN:VEVENT\nDTSTART:${fmt(startDt)}\nDTEND:${fmt(endDt)}\nSUMMARY:${confirmation.data.service} - ${config.businessName}\nDESCRIPTION:${confirmation.data.service}${providerDisplayName ? ` with ${providerDisplayName}` : ''}\nEND:VEVENT\nEND:VCALENDAR`;
                  const icsBlob = new Blob([icsContent], { type: 'text/calendar' });
                  const icsUrl = URL.createObjectURL(icsBlob);

                  // Detect device/platform
                  const ua = navigator.userAgent || '';
                  const isIOS = /iPhone|iPad|iPod/.test(ua);
                  const isMac = /Macintosh/.test(ua) && 'ontouchend' in document; // iPad with desktop UA
                  const isAndroid = /Android/.test(ua);
                  const isApple = isIOS || isMac;

                  // Apple icon
                  const appleIcon = <svg width="18" height="18" viewBox="0 0 24 24" fill="#1a1a1a"><path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.8-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z"/></svg>;
                  // Google icon
                  const googleIcon = <svg width="18" height="18" viewBox="0 0 24 24"><path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 01-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z"/><path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/><path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/><path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/></svg>;
                  // Calendar icon (generic)
                  const calIcon = <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><rect x="3" y="4" width="18" height="18" rx="2" ry="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></svg>;

                  const btnStyle = { display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, width: '100%', padding: '12px 16px', borderRadius: 10, border: 'none', fontSize: '0.9rem', fontWeight: 600, textDecoration: 'none', cursor: 'pointer', transition: '0.15s' };

                  if (isApple) {
                    // iPhone/iPad — show Apple Calendar button (downloads .ics which opens in Apple Calendar)
                    return (
                      <a href={icsUrl} download={`booking-${confirmation.data.date}.ics`}
                        style={{ ...btnStyle, background: '#1a1a1a', color: '#fff' }}>
                        {appleIcon} Add to Apple Calendar
                      </a>
                    );
                  }

                  if (isAndroid) {
                    // Android — show Google Calendar button
                    return (
                      <a href={googleUrl} target="_blank" rel="noopener noreferrer"
                        style={{ ...btnStyle, background: '#1a73e8', color: '#fff' }}>
                        {googleIcon} Add to Google Calendar
                      </a>
                    );
                  }

                  // Desktop — show all options
                  return (
                    <div style={{ display: 'flex', gap: 8, justifyContent: 'center' }}>
                      <a href={googleUrl} target="_blank" rel="noopener noreferrer"
                        style={{ ...btnStyle, flex: 1, background: '#fff', color: '#1a1a1a', border: '1px solid #dadce0' }}>
                        {googleIcon} Google
                      </a>
                      <a href={outlookUrl} target="_blank" rel="noopener noreferrer"
                        style={{ ...btnStyle, flex: 1, background: '#fff', color: '#1a1a1a', border: '1px solid #dadce0' }}>
                        {calIcon} Outlook
                      </a>
                      <a href={icsUrl} download={`booking-${confirmation.data.date}.ics`}
                        style={{ ...btnStyle, flex: 1, background: '#fff', color: '#1a1a1a', border: '1px solid #dadce0' }}>
                        {appleIcon} Apple
                      </a>
                    </div>
                  );
                })()}
              </div>

              <div style={{ borderTop: '1px solid #e5e7eb', padding: '16px 28px 20px', textAlign: 'center' }}>
                <div style={{ fontSize: '0.85rem', color: '#5f6368', marginBottom: 6 }}>Need to change?</div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8, alignItems: 'center', marginBottom: 16 }}>
                  <span onClick={() => { setConfirmation(null); setLookupValue(form.phone || form.email); setLookupField(form.phone ? 'phone' : 'email'); setShowMyBookings(true); }}
                    style={{ color: '#1a73e8', fontSize: '0.88rem', cursor: 'pointer', fontWeight: 500 }}>
                    View or cancel your booking
                  </span>
                  <span onClick={() => { setStep(1); setSelectedService(null); setSelectedProvider(''); setSelectedDate(''); setSelectedTime(''); setForm({ name: '', phone: '', email: '', notes: '' }); setConfirmation(null); }}
                    style={{ color: '#1a73e8', fontSize: '0.88rem', cursor: 'pointer', fontWeight: 500 }}>
                    Book another appointment
                  </span>
                </div>
                <button onClick={() => setConfirmation(null)}
                  style={{ background: 'none', border: 'none', color: '#1a73e8', fontSize: '0.92rem', fontWeight: 500, cursor: 'pointer', padding: '4px 8px' }}>
                  Close
                </button>
              </div>
            </div>
          </div>
        );
      })()}

      {renderMyBookingsModal()}

      {/* Footer */}
      <div style={{ textAlign: 'center', padding: '24px 0 80px', fontSize: '0.72rem', color: '#b0b0b0' }}>Powered by AINA</div>

      {/* Responsive CSS */}
      <style>{`
        .booking-cal-mobile { display: none !important; }
        .booking-cal-desktop { display: flex !important; }
        @media (max-width: 768px) {
          .booking-cal-mobile { display: block !important; }
          .booking-cal-desktop { display: none !important; }
          .booking-week-grid { overflow-x: auto; -webkit-overflow-scrolling: touch; }
        }
        .booking-week-grid::-webkit-scrollbar { display: none; }
        .booking-cal-mobile > div:last-child > div:last-child::-webkit-scrollbar { display: none; }
      `}</style>
    </div>
  );
}
